import Button from "@material-ui/core/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import MuiDialog from "@material-ui/core/Dialog"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import Divider from "@material-ui/core/Divider"
import Typography from "@material-ui/core/Typography"
import clsx from "clsx"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import TableContainer from "@material-ui/core/TableContainer"
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles"
import { AlertCircle, AlertTriangle } from "react-feather"
import { Alert, AlertTitle } from "@material-ui/lab"
import {
  useBillingSummaryAndCostEstimateQuery,
  type BillingSummaryResponse,
  type CostEstimationScenario,
} from "../../organisations/api"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { BillingSettingsFormDialog } from "../../organisations/components/billing-settings-form/dialog"

type BillingAlertProps = {
  orgId: string
  costEstimationScenarios?: CostEstimationScenario[]
  translations: any
}
type BillingStatus = BillingSummaryResponse["billingStatus"]
type CostEstimateType = BillingSummaryResponse["estimateBasedOnScenarios"]
type Tier = {
  name: string
  quantity: number
  price: number
  total: number
}

const estimateToTierSummary = (
  estimate: CostEstimateType,
  translations: { basic: string; projectManager: string }
): Tier[] => {
  const summary: Tier[] = []

  if (estimate?.basicUsers) {
    summary.push({
      name: translations.basic,
      quantity: estimate.basicUsers || 0,
      price: estimate.basicUserCost / (estimate.basicUsers || 1),
      total: estimate.basicUserCost,
    })
  }

  if (estimate?.projectManagers) {
    summary.push({
      name: translations.projectManager,
      quantity: estimate.projectManagers || 0,
      price: estimate.projectManagerCost / (estimate.projectManagers || 1),
      total: estimate.projectManagerCost,
    })
  }

  return summary
}

const CostEstimate = ({ costEstimate }: { costEstimate: CostEstimateType }) => {
  const { t } = useTranslation("membership")
  const { t: tOrg } = useTranslation("org")
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false)

  if (!costEstimate) return null

  const estimated = t("estimatedTitle", { totalCost: costEstimate.totalCost })
  const estimatedDisclaimer = t("estimatedDisclaimer")
  const viewBreakdown = t("viewBreakdown")

  const openDialog = () => setBreakdownDialogOpen(true)
  const closeDialog = () => setBreakdownDialogOpen(false)

  return (
    <>
      <Typography variant="subtitle1" color="textPrimary">
        {estimated}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {estimatedDisclaimer}
        <Button
          size="small"
          color="primary"
          onClick={openDialog}
          style={{ fontSize: 10, marginLeft: 4, textDecoration: "none" }}
        >
          {viewBreakdown}
        </Button>
      </Typography>
      <TierSummaryDialog
        open={breakdownDialogOpen}
        onClose={closeDialog}
        summary={estimateToTierSummary(costEstimate, {
          basic: tOrg("basicAccess"),
          projectManager: tOrg("fullAccess"),
        })}
      />
    </>
  )
}

const WithinFreeSeatLimitAlert = ({ billingStatus }: { billingStatus: BillingStatus }) => {
  const classes = useStyles()
  const { t } = useTranslation("membership")

  const title = t("enjoyFree")
  const { seatsUsed, seatLimit } = billingStatus
  const description = t("enjoyFreeDescription", { seatsUsed, seatLimit })

  return (
    <Alert
      icon={<span className="iconEmoji">🎁</span>}
      severity="info"
      className={clsx(classes.freeTierAlert, "lastItem")}
      data-test="discount-alert"
    >
      <AlertTitle data-test="discount-alert-title">{title}</AlertTitle>
      <Typography variant="body2" color="textSecondary">
        {description}
      </Typography>
    </Alert>
  )
}

const FreeSeatLimitReachedAlert = ({
  billingStatus,
  costEstimate,
}: {
  billingStatus: BillingStatus
  costEstimate: CostEstimateType
}) => {
  const classes = useStyles()
  const { t } = useTranslation("membership")

  const title = t("billingPolicyTitle")
  const description = t("billingPolicyDescription", { seatLimit: billingStatus.seatLimit })

  return (
    <div className={classes.alertRoot}>
      <div className="alertIcon">
        <AlertCircle size={24} />
      </div>
      <div>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
        <Divider style={{ margin: "16px 0" }} />
        <CostEstimate costEstimate={costEstimate} />
      </div>
    </div>
  )
}

const OverFreeSeatLimitAlert = ({
  orgId,
  billingStatus,
  costEstimate,
  refetchSummary,
}: {
  orgId: string
  billingStatus: BillingStatus
  costEstimate: CostEstimateType
  refetchSummary: () => void
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const { t } = useTranslation("membership")
  const [billingSettingsDialogOpen, setBillingSettingsDialogOpen] = useState(false)

  const openBillingSettingsDialog = () => setBillingSettingsDialogOpen(true)
  const closeBillingSettingsDialog = () => {
    setBillingSettingsDialogOpen(false)
    refetchSummary() // Refetch the billing summary to get the updated billing status after the user updates their billing settings
  }

  const title = t("billingInfoTitle")
  const description = t("billingInfoDescription", { seatLimit: billingStatus.seatLimit })

  return (
    <div className={classes.alertRoot}>
      <div className="alertIcon">
        <AlertTriangle color={theme.palette.error.main} size={20} />
      </div>
      <div>
        <Typography variant="subtitle1" style={{ color: theme.palette.error.main }} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
        <div style={{ marginTop: 16 }}>
          <Button variant="outlined" onClick={openBillingSettingsDialog}>
            {t("fillBillingInfo")}
          </Button>
          <BillingSettingsFormDialog
            orgId={orgId}
            open={billingSettingsDialogOpen}
            onClose={closeBillingSettingsDialog}
          />
        </div>
        <Divider style={{ margin: "16px 0" }} />
        <CostEstimate costEstimate={costEstimate} />
      </div>
    </div>
  )
}

const MemberTierSummaryTable = ({ summary }: { summary: Tier[] }) => {
  const classes = useStyles()
  const { t } = useTranslation("membership")

  return (
    <TableContainer className={classes.memberTierTableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow className={classes.memberTierTableRow}>
            <TableCell className={classes.memberTierTableHeaderCell}>{t("accessLevel")}</TableCell>
            <TableCell align="right" className={classes.memberTierTableHeaderCell}>
              {t("member")}
            </TableCell>
            <TableCell align="right" className={classes.memberTierTableHeaderCell}>
              {t("price")}
            </TableCell>
            <TableCell align="right" className={classes.memberTierTableHeaderCell}>
              {t("total")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.map((plan) => (
            <>
              <TableRow key={plan.name} className={classes.memberTierTableRow}>
                <TableCell>{plan.name}</TableCell>
                <TableCell align="right">{plan.quantity}</TableCell>
                <TableCell align="right">{plan.price}</TableCell>
                <TableCell align="right" className={classes.memberTierTableTotalCell}>
                  {plan.total}
                </TableCell>
              </TableRow>
            </>
          ))}
          <TableRow className={classes.memberTierTableRow}>
            <TableCell colSpan={3} align="right" className={classes.memberTierTableTotalLabelCell}>
              {t("total")}
            </TableCell>
            <TableCell align="right" className={classes.memberTierTableGrandTotalCell}>
              {summary.reduce((acc, plan) => acc + plan.total, 0)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const TierSummaryDialog = ({ open, onClose, summary }: { open: boolean; onClose: () => void; summary: Tier[] }) => {
  const { t } = useTranslation("membership")

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("costBreakdownTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
          {t("costBreakdownDescription")}
        </Typography>
        <MemberTierSummaryTable summary={summary} />
      </DialogContent>
    </MuiDialog>
  )
}

export const BillingAlert = (props: BillingAlertProps) => {
  const classes = useStyles()
  const {
    data: billingSummaryWithCostEstimate,
    isLoading,
    refetch: refetchSummary,
  } = useBillingSummaryAndCostEstimateQuery(
    {
      orgId: props.orgId,
      scenarios: props.costEstimationScenarios || [],
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  if (!billingSummaryWithCostEstimate || isLoading) {
    return (
      <div className={classes.alertRoot}>
        <CircularProgress size={24} color="primary" style={{ margin: "auto" }} />
      </div>
    )
  }

  const { billingStatus, estimateBasedOnScenarios } = billingSummaryWithCostEstimate
  const { hasAgreedBillingPolicy, seatLimit, seatsUsed } = billingStatus

  const isWithinFreeSeatLimit = seatsUsed < seatLimit // Placeholder for actual logic to determine if the user is within the free seat limit
  const hasReachedSeatLimit = seatsUsed === seatLimit // Placeholder for actual logic to determine if the user is reaching the free seat limit
  const isOverFreeSeatLimit = seatsUsed > seatLimit // Placeholder for actual logic to determine if the user is over the free seat limit

  if (hasAgreedBillingPolicy) {
    return null
  }

  if (isWithinFreeSeatLimit) {
    return <WithinFreeSeatLimitAlert billingStatus={billingStatus} />
  }

  if (hasReachedSeatLimit) {
    return <FreeSeatLimitReachedAlert billingStatus={billingStatus} costEstimate={estimateBasedOnScenarios} />
  }

  if (isOverFreeSeatLimit || true) {
    return (
      <OverFreeSeatLimitAlert
        orgId={props.orgId}
        billingStatus={billingStatus}
        costEstimate={estimateBasedOnScenarios}
        refetchSummary={refetchSummary}
      />
    )
  }
}

const useStyles = makeStyles((theme) => {
  return {
    freeTierAlert: {
      borderRadius: 0,
      borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
      background: alpha(theme.palette.info.main, 0.15),
      "& .iconEmoji": {
        padding: 8,
        background: "white",
        borderRadius: "50%",
        height: 48,
        width: 48,
        textAlign: "center",
      },
      "&:first-child": {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
      },
      "&.lastItem": {
        borderBottom: "none",
      },
    },
    alertRoot: {
      padding: theme.spacing(2),
      backgroundColor: alpha(theme.palette.error.main, 0.035),
      border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
      borderRadius: theme.shape.borderRadius,
      display: "flex",
      gap: theme.spacing(2),
      "& .alertIcon": {
        marginTop: theme.spacing(0.5),
      },
      "& .button": {
        marginTop: theme.spacing(2),
      },
    },
    memberTierTableContainer: {
      marginTop: 8,
      marginBottom: 8,
      borderCollapse: "collapse",
    },
    memberTierTableHeaderCell: {
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      color: "grey",
    },
    memberTierTableTotalCell: {
      fontWeight: 600,
    },
    memberTierTableTotalLabelCell: {
      fontWeight: 600,
    },
    memberTierTableGrandTotalCell: {
      fontWeight: 700,
    },
    memberTierTableRow: {
      background: theme.palette.grey[50],
      "& td": {
        borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
        borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
      },
    },
  }
})
